import type { BausteinArt } from "../../../services/pdfBausteinService";

/**
 * Platzieren mit Vorschau (Plan 02.10.2026, PR 4) — die reinen Teile.
 *
 * Unterschrift, Stempel und ein Bild aus der Zwischenablage (Strg+V) landen
 * alle auf demselben Weg im PDF: erst ein **Auftrag** (welches Bild, wie breit),
 * dann hängt das Bild halbtransparent am Mauszeiger (`PlatzierEbene`), und der
 * Klick setzt es als PDF.js-Stempel (`stempelSetzen`). Ohne React, damit die
 * Rechnung für die Vorschaugröße und die Breitenstufen direkt testbar sind.
 */

export type PlatzierArt = BausteinArt | "bild";

export type PlatzierAuftrag = {
    art: PlatzierArt;
    /** Das Bild, das als PDF.js-Stempel ins PDF wandert. */
    bild: Blob;
    /** Object-URL desselben Bilds — für die Vorschau am Mauszeiger. */
    url: string;
    /** Gewünschte Breite im PDF in pt; Strg+Mausrad ändert sie stufenweise. */
    breitePt: number;
};

/** Startbreite im PDF in pt: Unterschrift ≈ 150, Stempel ≈ 160, Bild aus der Zwischenablage 200. */
export const BREITE_PT: Record<PlatzierArt, number> = { unterschrift: 150, stempel: 160, bild: 200 };
export const BREITE_MIN_PT = 30;
export const BREITE_MAX_PT = 1200;
/** Eine Mausrad-Stufe = 10 % größer bzw. kleiner. */
const STUFE = 1.1;
/** Größter Anteil der Seite, den ein frisch gesetzter Stempel einnehmen darf. */
export const MAX_ANTEIL = 0.9;

/** Nächste Breite nach einer Mausrad-Stufe — ganze pt, innerhalb der Grenzen. */
export function breiteStufe(breitePt: number, richtung: 1 | -1): number {
    const neu = richtung > 0 ? breitePt * STUFE : breitePt / STUFE;
    return Math.min(BREITE_MAX_PT, Math.max(BREITE_MIN_PT, Math.round(neu)));
}

/** Hinweiszeile unter der Leiste, solange platziert wird. */
export function platzierHinweis(art: PlatzierArt): string {
    const was = art === "unterschrift" ? "die Unterschrift" : art === "stempel" ? "der Stempel" : "das Bild";
    return `Klicken Sie auf die Stelle, an der ${was} stehen soll — Strg+Mausrad ändert die Größe, Esc bricht ab.`;
}

/** Gewünschte Breite in pt + Bildmaße → `width/height` des PDF.js-Editors als Anteil der Seite. */
export function stempelMasse(
    breitePt: number,
    bild: { w: number; h: number },
    seiteBreitePt: number,
    seiteHoehePt: number,
): { width: number; height: number } {
    const verhaeltnis = bild.w > 0 ? bild.h / bild.w : 1;
    let w = breitePt;
    let h = breitePt * verhaeltnis;
    const faktor = Math.min(1, (MAX_ANTEIL * seiteBreitePt) / w, (MAX_ANTEIL * seiteHoehePt) / h);
    w *= faktor;
    h *= faktor;
    return { width: w / seiteBreitePt, height: h / seiteHoehePt };
}

/**
 * Maße der Vorschau in CSS-Pixeln — genau so groß, wie das Bild nach dem Klick
 * auf dieser Seite wird (Breite in pt × Maßstab, gedeckelt wie `stempelMasse`).
 * Ohne bekannte Bildmaße (Bild lädt noch) nur die Breite; die Höhe ergibt sich
 * dann aus dem Seitenverhältnis des Bildes selbst.
 */
export function vorschauMasse(
    breitePt: number,
    bild: { w: number; h: number } | null,
    seite: { breitePt: number; hoehePt: number; cssProPt: number },
): { w: number; h: number | null } {
    if (!bild) {
        const w = Math.min(breitePt, MAX_ANTEIL * seite.breitePt);
        return { w: w * seite.cssProPt, h: null };
    }
    const m = stempelMasse(breitePt, bild, seite.breitePt, seite.hoehePt);
    return { w: m.width * seite.breitePt * seite.cssProPt, h: m.height * seite.hoehePt * seite.cssProPt };
}
